/** Supplier rates are not currency amounts: keep four rate decimals until line rounding. */
export type SupplierDecimalInput = string | number;
const RATE_FACTOR = 10_000n;
const QUANTITY_FACTOR = 1_000_000n;
const MAX_RATE = 999_999_999_999_9999n; // numeric(16,4): same 12 integer digits as money
const MAX_QUANTITY = 99_999_999_999_999_999999n; // numeric(20,6)
const MAX_AMOUNT_CENTS = 99_999_999_999_999n; // numeric(14,2)

function parseScaled(value: unknown, scale: number, label: string, maximum: bigint): bigint {
  if (typeof value !== "string" && typeof value !== "number") {
    throw new Error(`${label} must be a non-negative decimal.`);
  }
  const raw = String(value).trim();
  if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error(`${label} must be a non-negative decimal.`);
  const [whole, fraction = ""] = raw.split(".");
  if (fraction.length > scale) throw new Error(`${label} supports at most ${scale} decimal places.`);
  const result = BigInt(whole) * 10n ** BigInt(scale) + BigInt(fraction.padEnd(scale, "0"));
  if (result > maximum) throw new Error(`${label} exceeds the supported range.`);
  return result;
}

function decimal(value: bigint, scale: number, minimumDecimals = 0): string {
  const sign = value < 0n ? "-" : "";
  const absolute = value < 0n ? -value : value;
  const factor = 10n ** BigInt(scale);
  const fraction = (absolute % factor).toString().padStart(scale, "0").replace(/0+$/, "").padEnd(minimumDecimals, "0");
  return `${sign}${absolute / factor}${fraction ? `.${fraction}` : ""}`;
}

export function unitPriceToScaled(value: unknown): bigint {
  return parseScaled(value, 4, "Unit cost", MAX_RATE);
}

export function scaledToUnitPrice(value: bigint): string {
  return decimal(value, 4, 2);
}

export function formatSupplierUnitPrice(value: unknown): string {
  return scaledToUnitPrice(unitPriceToScaled(value));
}

export function supplierQuantityToMicrounits(value: unknown): bigint {
  return parseScaled(value, 6, "Quantity", MAX_QUANTITY);
}

/** Nonnegative half-up rounding is performed once per extended line, never on its rate. */
export function calculateSupplierLineMoney(quantity: unknown, unitCost: unknown) {
  const quantityMicrounits = supplierQuantityToMicrounits(quantity);
  const unitCostScaled = unitPriceToScaled(unitCost);
  const divisor = QUANTITY_FACTOR * RATE_FACTOR / 100n;
  const lineTotalCents = (quantityMicrounits * unitCostScaled + divisor / 2n) / divisor;
  if (lineTotalCents > MAX_AMOUNT_CENTS) throw new Error("Line amount exceeds the supported range.");
  return {
    quantity: decimal(quantityMicrounits, 6),
    quantityMicrounits,
    unitCost: scaledToUnitPrice(unitCostScaled),
    unitCostScaled,
    lineTotal: decimal(lineTotalCents, 2, 2),
    lineTotalCents,
  };
}

/** Sum already rounded line amounts; do not round a raw document grand total. */
export function sumSupplierLineAmounts(lines: ReadonlyArray<{ lineTotal: unknown }>): string {
  const cents = lines.reduce((sum, line) => sum + parseScaled(line.lineTotal, 2, "Line amount", MAX_AMOUNT_CENTS), 0n);
  if (cents > MAX_AMOUNT_CENTS) throw new Error("Document total exceeds the supported range.");
  return decimal(cents, 2, 2);
}